import React, { useState, useEffect, memo, useContext, useMemo } from "react";
import { Tabs, Select, Row, Col } from "antd";
import ResolutionRadioButtons from "./ResolutionRadioButtons";
import "./TwoDImages.scss";
import InfiniteScrollGallery from "./InfiniteScrollGallery";
import TwoDImagesContext from "../../ContextFiles/TwoDImagesContext";

const { TabPane } = Tabs;

const TabContent = memo(({ images, loading, showModal, onAIImageClick, type, hasAnyFinalImages }) => {
  return (
    <InfiniteScrollGallery
      images={images}
      loading={loading}
      onImageClick={(index) => showModal(index)}
      onAIImageClick={onAIImageClick}
      type={type}
      hasAnyFinalImages={hasAnyFinalImages}
    />
  );
});

const ImageTabs = ({
  filteredImages,
  imagesInitialLoading,
  showModal,
  setImageType,
  Type,
  imagesCount,
  hasAnyFinalImages,
  onAIImageClick,
  hideTabs = false,
  distinctUsernames = [],
}) => {
  const { formState, setFormState } = useContext(TwoDImagesContext);
  const isAiCustomer = localStorage.getItem('is_ai_customer') === 'true';
  const isEnterprise = localStorage.getItem('is_enterprise_user') === 'true' || localStorage.getItem('user_plan') === 'enterprise';
  const currentUsername = localStorage.getItem('username');

  // Sort usernames: current user first (if exists), then others alphabetically
  const sortedUsernames = useMemo(() => {
    if (!distinctUsernames.length) return [];
    const otherUsers = distinctUsernames.filter(u => u !== currentUsername).sort();
    if (currentUsername && distinctUsernames.includes(currentUsername)) {
      return [currentUsername, ...otherUsers];
    }
    return otherUsers;
  }, [distinctUsernames, currentUsername]);

  // Check if there are any users other than the current user
  // Only show dropdown and tags when there are multiple users
  const hasOtherUsers = useMemo(() => {
    if (!distinctUsernames.length) return false;
    if (distinctUsernames.length === 1) {
      // If only one user, check if it's different from current user
      return distinctUsernames[0] !== currentUsername;
    }
    return true;
  }, [distinctUsernames, currentUsername]);
  
  const [activeKey, setActiveKey] = useState(() => {
    // For non-AI customers with ai/final_images type, use "ai" as the tab key
    if (!isAiCustomer && Type && ["ai", "final_images"].includes(Type)) {
      return "ai";
    }
    return Type || (isAiCustomer ? "ai" : "all");
  });
  
  // For non-AI customers to switch between 1K and Final
  const [aiImageSubType, setAiImageSubType] = useState(() => {
    if (!isAiCustomer && Type && ["ai", "final_images"].includes(Type)) {
      return Type;
    }
    return "ai";
  });

  // Update local state when Type changes
  useEffect(() => {
    const defaultType = isAiCustomer ? "ai" : "all";
    const selectedType = Type || defaultType;
    
    if (!isAiCustomer && ["ai", "final_images"].includes(selectedType)) {
      // For non-AI customers, both "ai" and "final_images" types should keep the "ai" tab active
      setActiveKey("ai");
      setAiImageSubType(selectedType);
    } else {
      // For all other types (including silo, lifestyle, spin_360, mp4), use the type as the tab key
      setActiveKey(selectedType);
    }
  }, [Type, isAiCustomer]);

  const handleTabChange = (key) => {
    setActiveKey(key);
    if (key === "ai" && !isAiCustomer) {
      // For non-AI customers on AI Images tab, use the dropdown selection
      setImageType(aiImageSubType);
    } else {
      setImageType(key); // Update external state as well
    }
  };

  // Set default username filter to current user if available, otherwise show "All"
  // Only set default when on AI-related tabs (ai or final_images) and there are other users
  useEffect(() => {
    const isAITab = Type === 'ai' || Type === 'final_images' || 
                    (!isAiCustomer && Type && ["ai", "final_images"].includes(Type));
    
    if (isAITab && hasOtherUsers && formState.username === undefined) {
      if (currentUsername && distinctUsernames.includes(currentUsername)) {
        // Set to current user if available
        setFormState((prev) => ({
          ...prev,
          username: currentUsername,
        }));
      } else {
        // If current user not available, set to empty string (shows "All")
        setFormState((prev) => ({
          ...prev,
          username: "",
        }));
      }
    }
  }, [hasOtherUsers, distinctUsernames, currentUsername, Type, isAiCustomer, formState.username, setFormState]);



  return (
    <>
      {hideTabs ? (
        // When tabs are hidden, just show the gallery directly
        <TabContent
          type={Type || "ai"}
          images={filteredImages}
          loading={imagesInitialLoading}
          showModal={showModal}
          onAIImageClick={onAIImageClick}
          hasAnyFinalImages={hasAnyFinalImages}
        />
      ) : (
        <Tabs
          activeKey={activeKey}
          className="product-tab library"
          tabBarGutter={16}
          onChange={handleTabChange} // Update local state on tab change
        >
          {/* Non-AI Customer Tabs Order: All, Favorites, Silo, Lifestyle, AI Images, 360, MP4 */}
          {!isAiCustomer && (
            <>
              <TabPane tab={`All (${imagesCount["all"]})`} key="all">
                <ResolutionRadioButtons tabName="all" />
                <TabContent
                  type="all"
                  images={filteredImages}
                  loading={imagesInitialLoading}
                  showModal={showModal}
                  onAIImageClick={onAIImageClick}
                  hasAnyFinalImages={hasAnyFinalImages}
                />
              </TabPane>
              
              <TabPane tab={`Favorites (${imagesCount["favourites"]})`} key="favourites">
                <ResolutionRadioButtons tabName="favourites" />
                <TabContent
                  type="favourites"
                  images={filteredImages}
                  loading={imagesInitialLoading}
                  showModal={showModal}
                  onAIImageClick={onAIImageClick}
                  hasAnyFinalImages={hasAnyFinalImages}
                />
              </TabPane>
              
              <TabPane tab={`Silo Images (${imagesCount["silo"]})`} key="silo">
                <ResolutionRadioButtons tabName="silo" />
                <TabContent
                  type="silo"
                  images={filteredImages}
                  loading={imagesInitialLoading}
                  showModal={showModal}
                  onAIImageClick={onAIImageClick}
                  hasAnyFinalImages={hasAnyFinalImages}
                />
              </TabPane>
              
              <TabPane tab={`Lifestyle Images (${imagesCount["lifestyle"]})`} key="lifestyle">
                <ResolutionRadioButtons tabName="lifestyle" />
                <TabContent
                  type="lifestyle"
                  images={filteredImages}
                  loading={imagesInitialLoading}
                  showModal={showModal}
                  onAIImageClick={onAIImageClick}
                  hasAnyFinalImages={hasAnyFinalImages}
                />
              </TabPane>
              
              <TabPane tab={`AI Images (${imagesCount["ai"] + imagesCount["final_images"]})`} key="ai">
                <div key={`ai-images-${aiImageSubType}`}>
                  <Row>
                    <Col span={24}>
                      <div className="resolutions-tab manrope f-14 black-55 gutter-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Select
                            value={aiImageSubType}
                            onChange={(value) => {
                              setAiImageSubType(value);
                              setImageType(value);
                            }}
                            style={{ 
                              width: 180,
                              height: 40
                            }}
                            className="ai-images-dropdown"
                          >
                            <Select.Option value="ai">Previews</Select.Option>
                            <Select.Option value="final_images">Final Images</Select.Option>
                          </Select>
                          <span style={{ color: '#8c8c8c', fontSize: '14px', whiteSpace: 'nowrap' }}>
                            <span style={{ fontWeight: 600, color: '#262626' }}>{imagesCount["ai"]}</span> Generations
                            <span style={{ marginLeft: '16px' }}>
                              <span style={{ fontWeight: 600, color: '#262626' }}>{imagesCount["final_images"]}</span> Final Images
                            </span>
                          </span>
                        </div>
                        <ResolutionRadioButtons tabName={aiImageSubType} />
                      </div>
                    </Col>
                  </Row>
                  {console.log('hasOtherUsers', aiImageSubType)}
                  <TabContent
                    key={aiImageSubType}
                    type={aiImageSubType}
                    images={filteredImages}
                    loading={imagesInitialLoading}
                    showModal={showModal}
                    onAIImageClick={onAIImageClick}
                    hasAnyFinalImages={hasAnyFinalImages}
                  />
                </div>
              </TabPane>
              
              <TabPane tab={`Videos (${imagesCount["ai_video"]})`} key="ai_video">
                <ResolutionRadioButtons tabName="ai_video" />
                <TabContent
                  type="ai_video"
                  images={filteredImages}
                  loading={imagesInitialLoading}
                  showModal={showModal}
                  onAIImageClick={onAIImageClick}
                  hasAnyFinalImages={hasAnyFinalImages}
                />
              </TabPane>
              
              <TabPane tab={`360 View (${imagesCount["spin_360"]})`} key="spin_360">
                <ResolutionRadioButtons tabName="spin_360" />
                <TabContent
                  type="spin_360"
                  images={filteredImages}
                  loading={imagesInitialLoading}
                  showModal={showModal}
                  onAIImageClick={onAIImageClick}
                  hasAnyFinalImages={hasAnyFinalImages}
                />
              </TabPane>
              
              <TabPane tab={`MP4 Videos (${imagesCount["mp4"]})`} key="mp4">
                <ResolutionRadioButtons tabName="mp4" />
                <TabContent
                  type="mp4"
                  images={filteredImages}
                  loading={imagesInitialLoading}
                  showModal={showModal}
                  onAIImageClick={onAIImageClick}
                  hasAnyFinalImages={hasAnyFinalImages}
                />
              </TabPane>
            </>
          )}
          
          {/* AI Customer Tabs Order: Generations, Final Images, Favorites */}
          {isAiCustomer && (
            <>
              <TabPane tab={`Previews (${imagesCount["ai"]})`} key="ai">
                <ResolutionRadioButtons tabName="ai" />
                <TabContent
                  type="ai"
                  images={filteredImages}
                  loading={imagesInitialLoading}
                  showModal={showModal}
                  onAIImageClick={onAIImageClick}
                  hasAnyFinalImages={hasAnyFinalImages}
                />
              </TabPane>
              
              <TabPane tab={`Final Images (${imagesCount["final_images"]})`} key="final_images">
                {hasAnyFinalImages && (
                  <ResolutionRadioButtons tabName="final_images" />
                )}
                <TabContent
                  type="final_images"
                  images={filteredImages}
                  loading={imagesInitialLoading}
                  showModal={showModal}
                  onAIImageClick={onAIImageClick}
                  hasAnyFinalImages={hasAnyFinalImages}
                />
              </TabPane>

              <TabPane tab={`Videos (${imagesCount["ai_video"]})`} key="ai_video">
                <ResolutionRadioButtons tabName="ai_video" />
                <TabContent
                  type="ai_video"
                  images={filteredImages}
                  loading={imagesInitialLoading}
                  showModal={showModal}
                  onAIImageClick={onAIImageClick}
                  hasAnyFinalImages={hasAnyFinalImages}
                />
              </TabPane>
              
              <TabPane tab={`Favorites (${imagesCount["favourites"]})`} key="favourites">
                <ResolutionRadioButtons tabName="favourites" />
                <TabContent
                  type="favourites"
                  images={filteredImages}
                  loading={imagesInitialLoading}
                  showModal={showModal}
                  onAIImageClick={onAIImageClick}
                  hasAnyFinalImages={hasAnyFinalImages}
                />
              </TabPane>
            </>
          )}
        </Tabs>
      )}
    </>
  );
};


export default ImageTabs;
